import { CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { I18nPluralPipe, LowerCasePipe } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import { provideIcons } from '@ng-icons/core';
import { lucideBook, lucideDraftingCompass, lucideFileText, lucidePencilRuler, lucidePlus } from '@ng-icons/lucide';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { ExpansionPanel } from '@shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { Aptitude } from '../../../référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Production } from '../../../référentiel-métier-processus/processus-aptitude/models/production.model';
import { AptitudeStore } from '../../../référentiel-métier-processus/processus-aptitude/store/aptitude.store';
import { Ressource } from '../../models/ressource.model';
import { RessourceTypeNames } from '../../services/ressource.service';
import { ProductionStore } from '../../store/production.store';
import { RessourceProfessionnelleItemComponent } from '../ressource-professionnelle-item/ressource-professionnelle-item.component';
import { RessourcesProfessionnellesFormComponent } from '../ressources-professionnelles-form/ressources-professionnelles-form.component';

@Component({
  selector: 'app-ressources-professionnelles-list',
  imports: [
    I18nPluralPipe,
    ChipComponent,
    HlmSeparatorImports,
    HlmIconImports,
    HlmButtonImports,
    ExpansionPanel,
    RessourceProfessionnelleItemComponent,
    LowerCasePipe,
    CdkDropListGroup,
    CdkDropList,
  ],
  templateUrl: './ressources-professionnelles-list.component.html',
  providers: [provideIcons({ lucideBook, lucideDraftingCompass, lucidePencilRuler, lucideFileText, lucidePlus })],
})
export class RessourcesProfessionnellesListComponent {
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #productionStore = inject(ProductionStore);
  readonly #sidepanelService = inject(SidepanelService);

  readonly production = input.required<Production>();
  readonly cartographie = input.required<Cartographie>();
  readonly peutModifier = input.required<boolean>();
  readonly aptitude = signal<Aptitude | undefined>(undefined);

  protected readonly ressourceMapping: { [k: string]: string } = {
    '=0': '# ressource professionnelle',
    '=1': '# ressource professionnelle',
    other: '# ressources professionnelles',
  };

  readonly ressourceTypes = RessourceTypeNames;

  constructor() {
    effect(() => {
      const idAptitude = this.production()?.idAptitude;
      if (idAptitude) {
        const aptitude = this.#aptitudeStore.entityMap()[idAptitude];
        this.aptitude.set(aptitude);
      }
    });
  }

  ajouterRessourceProfessionnelle(type?: string) {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une nouvelle ressource professionnelle',
      RessourcesProfessionnellesFormComponent,
      `Renseignez les ressources professionnelles rattachées à la production <span class="font-semibold">${this.production().nom}</span>.`,
      { cartographie: this.cartographie(), idProduction: this.production().id, type },
    );
    this.#sidepanelService.open();
  }

  dropRessource(event: CdkDragDrop<Ressource[]>) {
    if (event.previousIndex === event.currentIndex) return;

    const ordonnées = [...event.container.data];
    moveItemInArray(ordonnées, event.previousIndex, event.currentIndex);

    const idProduction = this.production().id;

    this.#productionStore.ordonnerRessources({
      idProduction,
      type: ordonnées[0].type,
      idsRessources: ordonnées.map((r) => r.id),
      // En cas d'échec (ex. liste périmée), on recharge la production pour afficher l'ordre réel.
      error: () => this.#productionStore.selection(idProduction),
    });
  }

  getRessourcesFiltrees(type: string) {
    return this.production()
      .ressources.filter((x) => x.type === type)
      .sort((a, b) => (a.indice ?? 0) - (b.indice ?? 0));
  }

  getIcon(type: string) {
    switch (type) {
      case 'Connaissance':
        return 'lucideBook';
      case 'Documentation':
        return 'lucideFileText';
      case 'Matériel':
        return 'lucidePencilRuler';
      case 'Habileté':
        return 'lucideDraftingCompass';
      default:
        return;
    }
  }
}
